import { z } from 'zod';
import { stringArraySchema, timestampSchema } from './shared';
import { articleBlockSchema } from './articleBlock';

// Drizzle's `text({ mode: 'json' })` round-trips arrays unchanged. Phase 4
// pivots `body` from z.string() (markdown) to z.array(articleBlockSchema)
// (typed block union) — every consumer (page renderer, admin editor, seed,
// migration script) reads/writes this shape.
const articleBodySchema = z.array(articleBlockSchema);

export const articleStatusSchema = z.enum(['draft', 'published']);

export const articleRowSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  date: z.string().min(1),
  tags: stringArraySchema,
  excerpt: z.string(),
  readTime: z.number().int().nonnegative(),
  featured: z.boolean(),
  body: articleBodySchema,
  coverImage: z.string().nullable(),
  status: articleStatusSchema.default('published'),
  // Server-owned newsletter send state — read-only to clients.
  newsletterSentAt: timestampSchema.nullable().optional(),
  newsletterCampaignId: z.number().int().nullable().optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

// POST body — slug is required (user-provided PK), timestamps + defaults omitted.
export const articleInsertSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  date: z.string().min(1),
  tags: stringArraySchema.optional(),
  excerpt: z.string().optional(),
  readTime: z.number().int().nonnegative().optional(),
  featured: z.boolean().optional(),
  body: articleBodySchema,
  coverImage: z.string().nullable().optional(),
  status: articleStatusSchema.optional(),
});

export const articleUpdateSchema = articleInsertSchema.partial();

export type ArticleRow = z.infer<typeof articleRowSchema>;
export type ArticleInsert = z.input<typeof articleInsertSchema>;
export type ArticleUpdate = z.input<typeof articleUpdateSchema>;
export type ArticleStatus = z.infer<typeof articleStatusSchema>;
